import { useData } from '@/data/context';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RouterContext, RouterContextType } from './context';

function createUid(): string {
	return Date.now().toString(36) + Math.floor(Math.pow(10, 12) + Math.random() * 9 * Math.pow(10, 12)).toString(36);
}

export const RouterProvider = ({ children }: { children: React.ReactNode }) => {
	const initialized = useRef(false);
	const { loadPage } = useData();
	const [isLoading, setLoading] = useState(false);
	const [isError, setError] = useState(false);

	const [data, setData] = useState<RouterContextType['data']>({} as RouterContextType['data']);

	const [keys, setKeys] = useState<RouterContextType['keys']>(window.history.state?.keys || {});
	const [backKeys, setBackKeys] = useState<RouterContextType['backKeys']>(window.history.state?.backKeys);

	const [url, setUrl] = useState(new URL(document.URL));

	const load = useCallback(
		async (url: URL) => {
			setLoading(true);
			setError(false);
			try {
				const data = await loadPage(url.pathname, url.search);
				setLoading(false);
				setUrl(url);
				return data;
			} catch (e) {
				setLoading(false);
				setError(true);
				console.error(e);
			}
		},
		[loadPage],
	);

	useEffect(() => {
		function onPop() {
			if (url.pathname === window.history.state?.pathname) {
				setUrl(new URL(document.URL));
				return;
			}

			load(new URL(document.URL)).then((data) => {
				if (!data) return;
				setData(data);
				setUrl(new URL(document.URL));
				setKeys(window.history.state?.keys || {});
				setBackKeys(window.history.state?.backKeys);
			});
		}
		window.onpopstate = () => setTimeout(onPop, 0);

		// initial load
		if (!initialized.current) {
			initialized.current = true;
			load(url).then((data) => {
				if (!data) return;
				if (!window.history.state?.keys) {
					const keys: { page: string; overlayPage?: string } = {
						page: createUid(),
					};
					if (data.overlayPage) {
						keys.overlayPage = createUid();
					}
					window.history.replaceState({ ...(window.history.state || { hash: url.hash, pathname: url.pathname }), keys }, '', url);
					setKeys(keys);
				}
				setData(data);
			});
		}
	}, [load, url]);

	const push = useCallback<RouterContextType['push']>(
		(href, doReplace) => {
			const url = new URL(href, document.URL);

			if (url.pathname === window.history.state?.pathname) {
				if (url.hash !== window.history.state.hash) {
					window.history.replaceState({ ...window.history.state, hash: url.hash }, '', url);
					setUrl(url);
				}
				return;
			}

			const backKeys = window.history.state?.keys;
			window.history[doReplace ? 'replaceState' : 'pushState']({ hash: url.hash, pathname: url.pathname }, '', url);
			load(new URL(url, document.URL)).then((data) => {
				if (!data) return;
				const newKeys = { ...keys };
				if (data.overlayPage) {
					newKeys.overlayPage = createUid();
				} else {
					delete newKeys.overlayPage;
					newKeys.page = createUid();
				}
				window.history.replaceState({ ...window.history.state, backKeys, keys: newKeys }, '', url);
				setKeys(newKeys);
				setBackKeys(backKeys);
				setData(data);
			});
		},
		[load, keys],
	);

	const replace = useCallback<RouterContextType['replace']>(
		(href) => {
			push(href, true);
		},
		[push],
	);

	const go = useCallback<RouterContextType['go']>((num) => window.history.go(num), []);

	const context = useMemo<RouterContextType>(
		() => ({
			isLoading,
			isError,
			data,
			keys,
			backKeys,
			url,
			push,
			replace,
			go,
		}),
		[isLoading, isError, data, keys, backKeys, url, push, go],
	);
	return <RouterContext.Provider value={context}>{children}</RouterContext.Provider>;
};
